import { memo, useEffect, useState } from 'react'
import { Button, Dialog, DialogTitle, DialogActions, Stack, DialogContent } from '@collaborate-health/uikit'
import {
  createContentSharing,
  createContentSharings,
  resetShareSessionDialog,
  setOwnerForSharing,
  setSelectedTargetContentForMerging,
  setTenantForSharing,
} from '@tabeeb/modules/sessionsPage/actions'
import { useDispatch, useSelector } from '@tabeeb/store'
import { ContentSharingModelType, SharedContentDataType } from '@tabeeb/contracts/main-api/swagger-main-api'
import {
  getSelectedContent,
  getSelectedTenant,
  getSelectedUser,
} from '@tabeeb/modules/sessionsPage/selectors/contentSharings'
import { getSelectedFormsReview } from '@tabeeb/modules/contentReviews/selectors'
import SharingTypeSelector from './SharingTypeSelector'
import ActiveReviews from './ActiveReviews'
import ActiveReviewsSwitch from './ActiveReviewsSwitch'
import DestinationTenant from './DestinationTenant'
import DestinationUser from './DestinationUser'
import RenameContentButton from './RenameContentButton'
import DestinationContent from './DestinationContent'
import CopyCheckInsSwitch from './CopyCheckInsSwitch'

interface ShareSessionDialogProps {
  open: boolean
  onClose: () => void
  contentSharingType: ContentSharingModelType
  isMultipleSessions?: boolean
  dataType?: SharedContentDataType
  session?: any
}

const ShareSessionDialog: React.FC<ShareSessionDialogProps> = ({
  open,
  onClose,
  contentSharingType = ContentSharingModelType.SharedByOwner,
  isMultipleSessions,
  dataType,
  session,
}) => {
  const dispatch = useDispatch()
  const [sharingType, setSharingType] = useState(contentSharingType)
  const [isUpdateActiveReview, setIsUpdateActiveReview] = useState(false)
  const [shouldCopyCheckIns, setShouldCopyCheckIns] = useState(false)
  const selectedTenant = useSelector((state) => getSelectedTenant(state, { contentSharingType: sharingType }))
  const selectedUser = useSelector((state) => getSelectedUser(state, { contentSharingType: sharingType }))
  const selectedTargetContent = useSelector((state) => getSelectedContent(state, { contentSharingType: sharingType }))
  const reviewWithSelectedForm = useSelector(getSelectedFormsReview)

  const isReview =
    sharingType === ContentSharingModelType.ForReview || sharingType === ContentSharingModelType.MergeForReview
  const isMergeForReview = sharingType === ContentSharingModelType.MergeForReview
  const isMerge = sharingType === ContentSharingModelType.ContentsMerging || isMergeForReview
  const isContinueButtonDisabled = isMerge ? !selectedTargetContent : !selectedUser

  useEffect(() => {
    if (isReview && reviewWithSelectedForm) {
      setSharingType(reviewWithSelectedForm.Type)
    }
  }, [isReview, reviewWithSelectedForm])

  const toggleUpdateActiveReview = (value: boolean) => {
    dispatch(resetShareSessionDialog())
    setIsUpdateActiveReview(value)
  }

  const handleSetSharingType = (newSharingType: ContentSharingModelType) => {
    toggleUpdateActiveReview(false)
    setSharingType(newSharingType)
  }

  const handleCloseDialog = () => {
    setIsUpdateActiveReview(false)
    setShouldCopyCheckIns(false)
    dispatch(resetShareSessionDialog())
    onClose()
  }

  const shareContent = () => {
    if (selectedTenant?.IsPredefined) {
      dispatch(setTenantForSharing(selectedTenant))
    }

    if (selectedUser?.IsPredefined) {
      dispatch(setOwnerForSharing(selectedUser))
    }

    if (selectedTargetContent?.IsPredefined) {
      dispatch(setSelectedTargetContentForMerging(selectedTargetContent))
    }

    if (isMultipleSessions) {
      dispatch(createContentSharings())
    } else {
      dispatch(createContentSharing({ type: sharingType, session, dataType, shouldCopyCheckIns }))
    }

    handleCloseDialog()
  }

  return (
    <Dialog open={open} onClose={handleCloseDialog} disableEscapeKeyDown fullWidth maxWidth='sm'>
      <DialogTitle>Destination</DialogTitle>
      <DialogContent>
        <SharingTypeSelector contentSharingType={sharingType} setSharingType={handleSetSharingType} />
        <CopyCheckInsSwitch
          contentSharingType={sharingType}
          shouldCopyCheckIns={shouldCopyCheckIns}
          toggleShouldCopyCheckIns={setShouldCopyCheckIns}
        />
        <ActiveReviewsSwitch
          isUpdateActiveReview={isUpdateActiveReview}
          toggleUpdateActiveReview={toggleUpdateActiveReview}
          contentSharingType={sharingType}
        />
        {isUpdateActiveReview && <ActiveReviews contentSharingType={sharingType} />}
        {!isUpdateActiveReview && (
          <Stack spacing={2}>
            <DestinationTenant contentSharingType={sharingType} />
            <DestinationUser contentSharingType={sharingType} />
            <DestinationContent contentSharingType={sharingType} />
            <RenameContentButton contentSharingType={sharingType} />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog} color='primary'>
          Cancel
        </Button>
        <Button onClick={shareContent} color='primary' variant='contained' disabled={isContinueButtonDisabled}>
          Continue
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default memo(ShareSessionDialog)

